import React from "react";
import { v2 } from "oc-threejs/short3";
import { Vector2 } from "three";
import { CoordinateSystem } from "./CoordinateSystem";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import { cachedEckeEpsilon, Strecke2 } from "../../arrangement/grundrissFunctions";
import { createGEcke, GEcke } from "../../arrangement/grundrissGerichteterGraph";
import * as _ from "lodash";
import { laengeDisplayPxMinimum } from "./Zaunlinien";

export function Winkel({ cs, winkel }: { cs: CoordinateSystem; winkel: GrundrissWinkel }) {
  const { punkt, angles, angleBetween } = winkel;
  const radiusPx = 100;
  const textRadiusPx = radiusPx * 0.55;
  const center = cs.pointToScreen(Maths3.v2To3xz(punkt));
  const spherical = (angle, radius) => v2(radius, 0).rotateAround(v2(), angle).add(center);
  const [start, end] = angles.map((a) => spherical(a, radiusPx));
  const textPosition = spherical(angles[0] + angleBetween / 2, textRadiusPx);
  const largeArcFlag = angleBetween > Math.PI ? "1" : "0";
  const pathD = `M ${start.x} ${start.y} A ${radiusPx} ${radiusPx} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;

  return (
    <g>
      <path d={pathD} className="svg-line-ruler" />
      <text x={textPosition.x} y={textPosition.y} className="svg-ruler-text">
        {I18N.formatFloat((angleBetween * 180) / Math.PI, 0)}°
      </text>
    </g>
  );
}

/**
 * Ein Winkel zwischen zwei Grundriss-Strecken einzig für die Winkelanzeige
 */
export interface GrundrissWinkel {
  punkt: Vector2;
  angles: [number, number];
  angleBetween: number;
}

export function createWinkel(cs: CoordinateSystem, strecken: Strecke2[]) {
  const minimaleWinkel: GrundrissWinkel[] = [];
  const ecken: GEcke[] = [];
  const cachedEcke = cachedEckeEpsilon(ecken, (p) => createGEcke(Commons.createId(), p));
  // abweichend von der Definition von GEcke hier ungerichtet (!)
  for (const strecke of strecken) {
    if (cs.lengthToScreen(strecke[0].distanceTo(strecke[1])) < laengeDisplayPxMinimum * 2) {
      continue;
    }
    const [a, b] = strecke.map(cachedEcke);
    a.adjazenz.add(b);
    b.adjazenz.add(a);
  }
  const nineteenDegree = (90 * Math.PI) / 180;
  const epsilon = (0.5 * Math.PI) / 180;
  const normalizeAngle = (angle: number) => (angle < 0 ? angle + 2 * Math.PI : angle);
  const angleBetween = (a: number, b: number) => normalizeAngle(b - a);
  for (const ecke of ecken) {
    if (ecke.adjazenz.length < 2) {
      continue;
    }
    const angles: number[] = [...ecke.adjazenz].map((e) =>
      Maths2.getDirection(ecke.punkt, e.punkt).angle()
    );
    angles.sort((a, b) => a - b);
    angles.push(angles[0]);
    const pairs: number[][] = Commons.pairwise(angles);
    // const nichtRechtwinkligePaare = pairs.filter(
    //   ([a, b]) => !Maths2.isMultipleOfAngle(angleBetween(a, b), nineteenDegree, epsilon)
    // );
    if (pairs.length > 0) {
      const [a, b] = _.minBy(pairs, ([a, b]) => angleBetween(a, b));
      minimaleWinkel.push({
        angles: [a, b],
        angleBetween: normalizeAngle(b - a),
        punkt: ecke.punkt,
      });
    }
  }
  return minimaleWinkel;
}
